import React, { useEffect } from 'react';
import { Box, Typography, Chip, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useTheme } from '../context/ThemeContext';
import { getPublishedStories } from '../data';
import { getCategoryById } from '../config/categories';
import { trackStoryView } from '../utils/analytics';
import SEO from '../components/SEO';
import Header from '../components/Header';
import MartiniBanner from '../components/MartiniBanner';

const Story: React.FC = () => {
  const { storyId } = useParams<{ storyId: string }>();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const story = getPublishedStories().find(s => s.id === storyId);
  const categories = story 
    ? story.categories.map(catId => getCategoryById(catId)).filter((cat): cat is NonNullable<typeof cat> => cat !== undefined)
    : [];

  useEffect(() => {
    if (story) {
      trackStoryView(story.id, story.title, story.city, story.intensity);
    }
  }, [story]);

  const colors = {
    bg: isDark ? '#0f0f0f' : '#f5f5f5',
    cardBg: isDark ? '#1a1a1a' : '#ffffff',
    headerBg: isDark ? '#161616' : '#fafafa',
    text: isDark ? '#e0e0e0' : '#1a1a1a',
    textSecondary: isDark ? '#999' : '#666',
    textMuted: isDark ? '#555' : '#aaa',
    border: isDark ? '#1a1a1a' : '#f0f0f0',
  };

  if (!story) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        <Header isMobile={isMobile} />
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Typography variant="h4" sx={{ color: colors.text }}>
            Story not found
          </Typography>
        </Box>
      </Box>
    );
  }

  // Get related stories
  const getRelatedStories = () => {
    const allStories = getPublishedStories().filter(s => s.id !== story.id);
    
    // Score stories based on similarity
    const scoredStories = allStories.map(s => {
      let score = 0;
      
      // Same category (highest weight)
      const sharedCategories = s.categories.filter(cat => story.categories.includes(cat));
      score += sharedCategories.length * 3;
      
      // Same tags (medium weight)
      const sharedTags = s.tags.filter(tag => story.tags.includes(tag));
      score += sharedTags.length * 2;
      
      // Same city (lower weight)
      if (s.city === story.city) score += 1;
      
      return { story: s, score };
    });
    
    // Sort by score and return top 4
    return scoredStories
      .sort((a, b) => b.score - a.score)
      .slice(0, 4)
      .map(item => item.story);
  };

  const relatedStories = getRelatedStories();

  // Generate breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dailystories.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": story.title,
        "item": `https://dailystories.fr/story/${story.id}`
      }
    ]
  };

  // Generate article structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": story.title,
    "description": story.excerpt,
    "author": {
      "@type": "Person",
      "name": "Marin",
      "url": "https://dailystories.fr/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Daily Stories",
      "url": "https://dailystories.fr",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailystories.fr/logo512.png"
      }
    },
    "datePublished": story.publishDate,
    "dateModified": story.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://dailystories.fr/story/${story.id}`
    },
    "articleSection": categories.map(c => c.name).join(', '),
    "keywords": story.tags.join(', '),
    "wordCount": story.content.split(/\s+/).length,
    "inLanguage": "en",
    "genre": "Erotic Fiction",
    "contentLocation": {
      "@type": "Place",
      "name": story.city
    },
    "audience": {
      "@type": "PeopleAudience",
      "suggestedMinAge": 18
    }
  };

  return (
    <>
      <SEO 
        title={`${story.title} | ${story.city} Erotic Story - Daily Stories`}
        description={`${story.excerpt} An intimate ${story.tags.join(', ')} story set in ${story.city}. ${story.readTime} read. LGBTQ+ friendly, 18+ only.`}
        keywords={`${story.title.toLowerCase()}, ${story.city.toLowerCase()} erotic story, ${story.tags.join(', ')}, intimate fiction, sensual story, adult fiction, ${categories.map(c => c.name.toLowerCase()).join(', ')}, lgbtq stories`}
        url={`https://dailystories.fr/story/${story.id}`}
      />
      
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleStructuredData) }}
      />
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: colors.bg
      }}>
        {/* Header */}
        <Header isMobile={isMobile} />

        {/* Story Content */}
        <Box 
          component="article"
          sx={{ maxWidth: '720px', mx: 'auto', px: { xs: 3, md: 0 }, py: { xs: 5, md: 8 } }}
        >
          {/* Story Header */}
          <Box component="header" sx={{ mb: 6, px: { xs: 0, md: 3 } }}>
            <Typography
              variant="overline"
              component="time"
              dateTime={story.publishDate}
              sx={{ 
                color: colors.textSecondary,
                display: 'block',
                mb: 2
              }}
            >
              {story.city} · {format(new Date(story.publishDate), 'MMMM d, yyyy')}
            </Typography>
            <Typography
              variant="h1"
              component="h1"
              sx={{ color: colors.text, mb: 3.5 }}
            >
              {story.title}
            </Typography>
            
            {/* Categories */}
            {categories.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                {categories.map((category) => (
                  <Chip
                    key={category.id}
                    label={category.name}
                    component="a"
                    href={`/category/${category.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/category/${category.id}`);
                    }}
                    sx={{
                      backgroundColor: isDark ? category.color.bgDark : category.color.bg,
                      color: isDark ? category.color.textDark : category.color.text,
                      fontWeight: 400,
                      fontSize: '0.8rem',
                      letterSpacing: '0.3px',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      transition: 'all 0.2s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        opacity: 0.85,
                        transform: 'translateY(-1px)'
                      }
                    }}
                  />
                ))}
              </Box>
            )}
            
            {/* Tags */}
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 3 }}>
              {story.tags.map((tag) => {
                const getTagStyle = (tag: string) => {
                  if (tag === '♀♀') {
                    return {
                      bg: isDark ? 'rgba(219, 112, 147, 0.15)' : 'rgba(219, 112, 147, 0.1)',
                      color: isDark ? '#FFB6C1' : '#C71585'
                    };
                  }
                  if (tag === '♂♂') {
                    return {
                      bg: isDark ? 'rgba(100, 149, 237, 0.15)' : 'rgba(100, 149, 237, 0.1)',
                      color: isDark ? '#87CEEB' : '#4169E1'
                    };
                  }
                  if (tag === '♂♀') {
                    return {
                      bg: isDark ? 'rgba(147, 112, 219, 0.15)' : 'rgba(147, 112, 219, 0.1)',
                      color: isDark ? '#DDA0DD' : '#8B008B'
                    };
                  }
                  return {
                    bg: isDark ? '#333' : '#f5f5f5',
                    color: isDark ? '#999' : '#666'
                  };
                };

                const tagStyle = getTagStyle(tag);

                return (
                  <Typography
                    key={tag}
                    component="span"
                    variant="caption"
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      backgroundColor: tagStyle.bg,
                      color: tagStyle.color,
                      borderRadius: '12px',
                      fontSize: '0.7rem',
                      letterSpacing: '0.5px',
                      fontWeight: 500,
                      userSelect: 'none'
                    }}
                  >
                    {tag}
                  </Typography>
                );
              })}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center', 
              pt: 2.5,
              borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
            }}>
              <Typography variant="caption" sx={{ color: colors.textMuted }}>
                {story.readTime}
              </Typography>
              <Typography sx={{ color: colors.textMuted, opacity: 0.5 }}>•</Typography>
              <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                {story.intensity === 0 ? (
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Non-erotic
                  </Typography>
                ) : (
                  [1, 2, 3].map((level) => (
                    <Typography
                      key={level}
                      component="span"
                      sx={{
                        fontSize: '0.9rem',
                        opacity: level <= story.intensity ? 1 : 0.25,
                        transition: 'opacity 0.2s ease'
                      }}
                    >
                      🔥
                    </Typography>
                  ))
                )}
              </Box>
            </Box>
          </Box>

          {/* Story Text */}
          <Box component="section" sx={{ px: { xs: 0, md: 3 } }}>
            {story.content.split('\n\n').map((paragraph, index) => (
              <Typography
                key={index}
                variant="body1"
                component="p"
                paragraph
                sx={{
                  color: colors.text,
                  ...(index === 0 && {
                    mt: 2,
                    '&::first-letter': {
                      float: 'left',
                      fontSize: { xs: '4rem', md: '4.5rem' },
                      lineHeight: { xs: '3.2rem', md: '3.5rem' },
                      fontWeight: 300,
                      mr: { xs: 1.5, md: 2 },
                      mt: { xs: 0.3, md: 0.5 },
                      color: colors.text,
                      fontFamily: 'Georgia, "Times New Roman", serif',
                    },
                  }),
                }}
              >
                {paragraph}
              </Typography>
            ))}
          </Box>

          {/* Martini Banner */}
          <MartiniBanner />

          {/* Related Stories */}
          {relatedStories.length > 0 && (
            <Box sx={{ mt: 8, mb: 6, px: { xs: 0, md: 3 } }}>
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  color: colors.text,
                  mb: 4,
                  fontSize: { xs: '1.5rem', md: '1.75rem' },
                  fontWeight: 400
                }}
              >
                More Like This
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {relatedStories.map((relatedStory) => {
                  const relatedCategories = relatedStory.categories
                    .map(catId => getCategoryById(catId))
                    .filter((cat): cat is NonNullable<typeof cat> => cat !== undefined);
                  
                  return (
                    <Box
                      key={relatedStory.id}
                      onClick={() => navigate(`/story/${relatedStory.id}`)}
                      sx={{
                        p: 3,
                        backgroundColor: colors.cardBg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateX(4px)',
                          borderColor: isDark ? '#333' : '#ddd',
                        }
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ color: colors.textMuted, fontSize: '0.7rem' }}
                      >
                        {relatedStory.city} · {format(new Date(relatedStory.publishDate), 'MMM d, yyyy')}
                      </Typography>
                      
                      <Typography
                        variant="h3"
                        sx={{
                          color: colors.text,
                          mt: 1,
                          mb: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 500
                        }}
                      >
                        {relatedStory.title}
                      </Typography>
                      
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textSecondary,
                          mb: 2,
                          fontSize: '0.9rem',
                          lineHeight: 1.6
                        }}
                      >
                        {relatedStory.excerpt}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {relatedCategories.slice(0, 2).map((category) => (
                          <Chip
                            key={category.id}
                            label={category.name}
                            size="small"
                            sx={{
                              backgroundColor: isDark ? category.color.bgDark : category.color.bg,
                              color: isDark ? category.color.textDark : category.color.text,
                              fontSize: '0.7rem',
                              height: '24px'
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Story;
